import React, { useEffect, useState } from 'react';
import { DollarSign, Loader2, Check, Undo2, AlertTriangle } from 'lucide-react';
import { admin } from '../services/api';
import { toast } from '../hooks/use-toast';

const PLANS = [
  { key: 'pbb_tier1_semiannual', label: '6-month membership', expected: 19.50 },
  { key: 'pbb_tier1_annual',     label: '12-month membership', expected: 39.00 },
];

/** Admin tile — swap live Stripe Tier-1 price IDs without a redeploy.
 *  DB override wins over env; clearing the field falls back to env.
 *  Every ID is validated against Stripe before it's saved. */
export default function AdminPlanPricesPanel() {
  const [state, setState] = useState(null);     // server response
  const [draft, setDraft] = useState({});       // user-edited IDs
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const s = await admin.getPlanPrices();
      setState(s);
      setDraft(Object.fromEntries(PLANS.map(p => [p.key, (s[p.key]?.override) || ''])));
    } catch (e) {
      toast({ title: 'Load failed', description: e?.response?.data?.detail || 'Try again' });
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const body = Object.fromEntries(PLANS.map(p => [p.key, (draft[p.key] || '').trim()]));
      const next = await admin.setPlanPrices(body);
      setState(next);
      setDraft(Object.fromEntries(PLANS.map(p => [p.key, (next[p.key]?.override) || ''])));
      toast({ title: 'Prices updated', description: 'The new IDs are live immediately for every new checkout.' });
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || 'Try again' });
    } finally {
      setBusy(false);
    }
  };

  const clearAll = async () => {
    setBusy(true);
    try {
      const next = await admin.setPlanPrices(Object.fromEntries(PLANS.map(p => [p.key, ''])));
      setState(next);
      setDraft(Object.fromEntries(PLANS.map(p => [p.key, ''])));
      toast({ title: 'Reset to env', description: 'DB overrides cleared — checkout now uses .env values.' });
    } catch (e) {
      toast({ title: 'Reset failed', description: e?.response?.data?.detail || 'Try again' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-plan-prices-panel">
      <div className="flex items-center gap-3">
        <DollarSign className="text-amber-400" size={20}/>
        <div>
          <h2 className="font-bold text-lg">Stripe Price Switcher</h2>
          <p className="text-xs text-neutral-400">Swap Tier-1 Stripe price IDs without a redeploy. DB value overrides the env setting; clear a field to fall back to env.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {PLANS.map(p => {
          const row = state?.[p.key];
          return (
            <div key={p.key} className="pbb-card p-5 space-y-3" data-testid={`price-card-${p.key}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-bold">{p.label}</h3>
                  <div className="text-[11px] text-neutral-500 mt-0.5">lookup_key: <code className="text-neutral-400">{p.key}</code></div>
                </div>
                {row && <SourceTag source={row.source}/>}
              </div>

              <label className="block">
                <span className="text-[10px] uppercase tracking-wider text-neutral-400 font-semibold">Live Stripe Price ID</span>
                <input
                  value={draft[p.key] || ''}
                  onChange={e => setDraft(d => ({ ...d, [p.key]: e.target.value }))}
                  placeholder={row?.env || 'price_...'}
                  data-testid={`price-input-${p.key}`}
                  className="mt-1 w-full bg-black border border-neutral-800 rounded-lg px-3 py-2 text-xs font-mono focus:border-amber-500 outline-none"
                />
                <div className="text-[10px] text-neutral-500 mt-1">Env fallback: <code>{row?.env || '(none)'}</code></div>
              </label>

              {/* Live Stripe details for the current effective ID */}
              {row?.effective && (
                <div className="rounded-lg bg-black/40 border border-neutral-800 p-3 text-xs" data-testid={`price-info-${p.key}`}>
                  {row.error ? (
                    <div className="text-red-300 inline-flex items-center gap-1"><AlertTriangle size={12}/> {row.error}</div>
                  ) : (
                    <>
                      <div className="flex items-baseline gap-2">
                        <span className="text-2xl font-black text-amber-300 tabular-nums">
                          ${(row.amount ?? 0).toFixed(2)}
                        </span>
                        <span className="text-neutral-400 text-[11px] uppercase">{row.currency}</span>
                      </div>
                      <div className="text-[11px] text-neutral-400 mt-0.5">
                        every {row.intervalCount || 1} {row.interval}{(row.intervalCount || 1) > 1 ? 's' : ''}
                        {row.active ? <span className="ml-2 text-emerald-400">· active</span> : <span className="ml-2 text-red-400">· inactive</span>}
                      </div>
                      {typeof p.expected === 'number' && Math.abs((row.amount || 0) - p.expected) > 0.01 && (
                        <div className="mt-2 inline-flex items-center gap-1 text-[10px] text-amber-300 bg-amber-500/10 border border-amber-500/40 rounded px-2 py-0.5">
                          <AlertTriangle size={10}/> Amount differs from the normal ${p.expected.toFixed(2)}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-end gap-2">
        <button onClick={clearAll} disabled={busy} data-testid="price-reset-env"
          className="inline-flex items-center gap-1 px-4 py-2 rounded-lg border border-neutral-700 text-sm text-neutral-200 hover:border-amber-500/60 disabled:opacity-50">
          <Undo2 size={14}/> Reset to env
        </button>
        <button onClick={save} disabled={busy} data-testid="price-save"
          className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-bold text-sm disabled:opacity-60">
          {busy ? <Loader2 className="animate-spin" size={14}/> : <Check size={14}/>} Save & apply
        </button>
      </div>
    </div>
  );
}

function SourceTag({ source }) {
  const style = {
    override: 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300',
    env:      'bg-sky-500/15 border-sky-500/40 text-sky-300',
    lookup:   'bg-neutral-500/15 border-neutral-500/40 text-neutral-300',
  }[source] || 'bg-neutral-500/15 border-neutral-500/40 text-neutral-300';
  const label = source === 'override' ? 'DB override' : source === 'env' ? 'ENV value' : 'Lookup fallback';
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full border text-[10px] font-black uppercase tracking-widest ${style}`}>
      {label}
    </span>
  );
}
